import { type ReactNode, useEffect, useState } from 'react'
import { X } from 'lucide-react'
import type { BadgeColor } from './StatusBadge'

const colorClasses: Record<BadgeColor, string> = {
  primary: 'bg-primary-50 text-link border-primary-100',
  success: 'bg-success-50 text-positive border-success-100',
  danger: 'bg-danger-50 text-negative border-danger-100',
  warning: 'bg-warning-50 text-notice border-warning-100',
  info: 'bg-info-50 text-informative border-info-100',
  secondary: 'bg-surface text-body border-border-subtle',
}

interface AlertProps {
  color: BadgeColor
  children: ReactNode
  dismissible?: boolean
  className?: string
  /** Se impostato, l'alert si chiude da solo dopo questi millisecondi (in aggiunta alla X manuale se dismissible). */
  autoDismissMs?: number
}

/** Sostituisce data-bs-dismiss="alert": stato di visibilità gestito con useState locale. */
export function Alert({ color, children, dismissible, className = '', autoDismissMs }: AlertProps) {
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (!autoDismissMs) return
    const timer = setTimeout(() => setDismissed(true), autoDismissMs)
    return () => clearTimeout(timer)
    // Solo autoDismissMs nelle dipendenze: il timer riparte solo se cambia la durata, non a ogni render di `children`
  }, [autoDismissMs])

  if (dismissed) return null
  return (
    <div className={`flex items-start justify-between gap-3 rounded-md border px-4 py-3 text-sm ${colorClasses[color]} ${className}`}>
      <div>{children}</div>
      {dismissible && (
        <button onClick={() => setDismissed(true)} className="shrink-0 cursor-pointer opacity-70 hover:opacity-100" aria-label="Chiudi">
          <X size={16} />
        </button>
      )}
    </div>
  )
}
